/**
 * Section 4 of 4. The messenger ships at launch (MSG-1), so this is a
 * real section waiting on its lane and not a "not built yet" placeholder
 * that will be deleted.
 *
 * The list mixes a pool's chat, a private chat, and a stand-alone chat with
 * no segmented control (`.agents/design-prompts/50-messages.md` §50.1 point
 * 4, the UI label for all three kinds is just "Chat"). The conversation
 * pane a row opens into is `ConversationPane` (ISSUE-280).
 */
import {
  useChatInvitationAccept,
  useChatInvitationDecline,
  useChatInvitationList,
  useChatListMine,
} from "@sportsmet/messaging-sdk/private";
import { Button, Input } from "@sportsmet/ui";
import { useQueryClient } from "@tanstack/react-query";
import {
  createFileRoute,
  useNavigate,
  useSearch,
} from "@tanstack/react-router";
import { type ReactNode, useEffect, useState } from "react";

import { requireSignedIn } from "../auth/requireSignedIn";
import {
  extractErrorCode,
  forgetQueryApiError,
  queryApiErrorIdentity,
  showApiError,
  showOfflineError,
  showQueryApiError,
} from "../errors/apiErrorBus";
import { ChatDetailsPanel } from "../features/chatDetails/ChatDetailsPanel";
import { MuteDialog } from "../features/chatDetails/MuteDialog";
import { ReportMessageDialog } from "../features/messageModeration/ReportMessageDialog";
import { ChatInvitationDialog } from "../features/messages/ChatInvitationDialog";
import {
  CHAT_SEARCH_LABEL,
  CHAT_SEARCH_PLACEHOLDER,
  NEW_MESSAGE_ENTRY_LABEL,
} from "../features/messages/chatListActionsCopy";
import {
  CHAT_ROW_ACTIONS_DIALOG,
  CHAT_ROW_MUTE_DIALOG,
  ChatRowActionsDialog,
} from "../features/messages/ChatRowActionsDialog";
import { ConversationPane } from "../features/messages/ConversationPane";
import { MessagesListBody } from "../features/messages/MessagesListBody";
import { resolveMessagesListState } from "../features/messages/messagesListState";
import {
  NEW_CHAT_DIALOG,
  NewChatDialog,
} from "../features/messages/NewChatDialog";
import {
  NEW_MESSAGE_DIALOG,
  NewMessageDialog,
} from "../features/messages/NewMessageDialog";
import { closeOverlay } from "../routeState/overlay";
import { validateMessagesSearch } from "../routeState/sectionSearch";
import { useOverlay } from "../routeState/useOverlay";
import { SectionEmptyState, SectionScreen } from "../shell/SectionScreen";
import { SectionTwoPane } from "../shell/SectionTwoPane";

// Polled at the same cadence as notification-api's inbox badge
// (messaging-api.md §6), so the two never drift out of sync with each other
// by more than one tick. This is a different question from ISSUE-280's
// per-conversation cadence, which is server-declared.
const MESSAGES_LIST_POLL_MS = 30_000;

const CHAT_INVITATION_DIALOG = "chat-invitation";
const REPORT_DIALOG = "report-message";
const CHAT_DETAILS_SHEET = "chat-details";

const MessagesScreen = (): ReactNode => {
  const search = useSearch({ from: "/messages" });
  const navigate = useNavigate({ from: "/messages" });
  const overlay = useOverlay();
  const isChatDetailsOpen = overlay.isOpen("sheet", CHAT_DETAILS_SHEET);
  const queryClient = useQueryClient();

  const chats = useChatListMine({}, { refetchInterval: MESSAGES_LIST_POLL_MS });
  const chatsErrorCode = extractErrorCode(chats.data);
  // ISSUE-1051 R2. Set only by the fan's Try again press, never read off
  // chats.isFetching: focus and reconnect refetches would otherwise flash the
  // skeleton for a read nobody asked for (same reasoning as PoolsListPane).
  const [isRetryingChats, setIsRetryingChats] = useState(false);
  const handleRetryChats = (): void => {
    // Re-arm the shared error modal so a repeat failure is a new episode.
    if (chatsErrorCode !== null) {
      forgetQueryApiError(
        queryApiErrorIdentity("chat/list-mine"),
        chatsErrorCode,
      );
    }
    setIsRetryingChats(true);
    void chats.refetch().then(
      () => {
        setIsRetryingChats(false);
      },
      () => {
        setIsRetryingChats(false);
      },
    );
  };
  const invitations = useChatInvitationList(
    {},
    { refetchInterval: MESSAGES_LIST_POLL_MS },
  );
  const invitationsErrorCode = extractErrorCode(invitations.data);
  const acceptInvitation = useChatInvitationAccept();
  const declineInvitation = useChatInvitationDecline();

  useEffect(() => {
    showQueryApiError(
      chats.data,
      queryApiErrorIdentity("chat/list-mine"),
      chatsErrorCode,
      chats.dataUpdatedAt,
    );
  }, [chatsErrorCode, chats.dataUpdatedAt]);

  useEffect(() => {
    showQueryApiError(
      invitations.data,
      queryApiErrorIdentity("chat-invitation/list"),
      invitationsErrorCode,
      invitations.dataUpdatedAt,
    );
  }, [invitationsErrorCode, invitations.dataUpdatedAt]);

  const chatItems = chats.data?.data?.items ?? null;
  const invitationItems = invitations.data?.data?.items ?? null;

  const state = resolveMessagesListState({
    chats: {
      isLoading: chats.isLoading,
      hasFailed: chats.isError || Boolean(chats.data?.error),
      isRetrying: isRetryingChats,
      items:
        chatItems === null
          ? null
          : chatItems.map((item) => ({
              chatId: item.chatId,
              chatType: item.chatType,
              title: item.title,
              lastMessagePreview: item.lastMessagePreview ?? null,
              lastMessageSenderDisplayName:
                item.lastMessageSenderDisplayName ?? null,
              lastMessageSenderIsSelf: item.lastMessageSenderIsSelf,
              lastMessageAtIso: item.lastMessageAtIso,
              unreadCount: item.unreadCount,
              isMuted: item.isMuted,
              isPendingApproval: item.isPendingApproval,
              avatarUrl: item.avatarUrl ?? null,
            })),
      hasPendingRequestBadge: chats.data?.data?.hasPendingRequestBadge ?? false,
    },
    invitations: {
      items:
        invitationItems === null
          ? null
          : invitationItems.map((item) => ({
              chatInvitationId: item.chatInvitationId,
              title: item.title,
              invitedByDisplayName: item.invitedByDisplayName,
              memberCount: item.memberCount,
            })),
    },
    nowMs: Date.now(),
    searchText: search.q ?? "",
  });

  // ISSUE-1362. The row the quick-actions or mute dialog is about, read off
  // the loaded list by the `?chatId=` the dialog carries.
  const actionChatItem =
    search.chatId === undefined
      ? null
      : ((chatItems ?? []).find((item) => item.chatId === search.chatId) ??
        null);

  const openConversation = (chatId: string): void => {
    void navigate({
      search: (previous) => ({
        ...closeOverlay(previous, {
          kind: "dialog",
          clearParams: ["chatId"],
        }),
        conversationId: chatId,
      }),
    });
  };

  // Shared by the pending-invitation row's own Accept/Decline (R3) and the
  // invitation preview dialog's Accept/Decline (R4/R5): the same two
  // endpoints answer both surfaces, so one pair of handlers drives them.
  const handleAcceptInvitation = async (
    chatInvitationId: string,
  ): Promise<void> => {
    try {
      const result = await acceptInvitation.mutateAsync({ chatInvitationId });
      if (result.error || !result.data) {
        showApiError(result);
        return;
      }
      void queryClient.invalidateQueries();
      const chatId = result.data.chatId;
      // `closeOverlay` REMOVES the overlay's keys rather than setting them to
      // `undefined` in place. `routeState/overlay.ts` explains why that
      // distinction matters, so never hand-spread `dialog: undefined` here.
      void navigate({
        search: (previous) => ({
          ...closeOverlay(previous, {
            kind: "dialog",
            clearParams: ["chatInvitationId"],
          }),
          conversationId: chatId,
        }),
      });
    } catch {
      showOfflineError();
    }
  };

  const handleDeclineInvitation = async (
    chatInvitationId: string,
  ): Promise<void> => {
    try {
      const result = await declineInvitation.mutateAsync({ chatInvitationId });
      if (result.error) {
        showApiError(result);
        return;
      }
      void queryClient.invalidateQueries();
      void navigate({
        replace: true,
        search: (previous) =>
          closeOverlay(previous, {
            kind: "dialog",
            clearParams: ["chatInvitationId"],
          }),
      });
    } catch {
      showOfflineError();
    }
  };

  const invitationRow =
    search.chatInvitationId === undefined
      ? null
      : ((invitationItems ?? []).find(
          (item) => item.chatInvitationId === search.chatInvitationId,
        ) ?? null);

  const listContent = (
    <MessagesListBody
      state={state}
      onSelectRequests={() => {
        void navigate({ to: "/messages/requests" });
      }}
      onSelectInvitation={(chatInvitationId) => {
        overlay.open({
          kind: "dialog",
          name: CHAT_INVITATION_DIALOG,
          params: { chatInvitationId },
        });
      }}
      onSelectChat={(chatId) => {
        void navigate({
          search: (previous) => ({
            ...previous,
            conversationId: chatId,
          }),
        });
      }}
      onAcceptInvitation={(chatInvitationId) => {
        void handleAcceptInvitation(chatInvitationId);
      }}
      onDeclineInvitation={(chatInvitationId) => {
        void handleDeclineInvitation(chatInvitationId);
      }}
      onRetry={handleRetryChats}
      onOpenChatActions={(chatId) => {
        overlay.open({
          kind: "dialog",
          name: CHAT_ROW_ACTIONS_DIALOG,
          params: { chatId },
        });
      }}
    />
  );

  return (
    <SectionScreen heading="Messages">
      <SectionTwoPane
        list={
          <div className="flex flex-col gap-3">
            <div className="flex items-center justify-between gap-2">
              <h2 className="sr-only">Chats</h2>
              <Button
                variant="secondary"
                data-testid="messages-new-message"
                onClick={() => {
                  overlay.open({ kind: "dialog", name: NEW_MESSAGE_DIALOG });
                }}
              >
                {NEW_MESSAGE_ENTRY_LABEL}
              </Button>
            </div>
            {state.phase === "ready" ? (
              <Input
                type="search"
                label={CHAT_SEARCH_LABEL}
                labelHidden
                placeholder={CHAT_SEARCH_PLACEHOLDER}
                autoComplete="off"
                value={search.q ?? ""}
                onChange={(event) => {
                  const q = event.target.value;
                  void navigate({
                    search: (previous) => ({
                      ...previous,
                      q: q === "" ? undefined : q,
                    }),
                    replace: true,
                  });
                }}
                data-testid="messages-search"
              />
            ) : null}
            {listContent}
          </div>
        }
        detail={
          search.conversationId === undefined ? null : (
            <ConversationPane
              // One mount per conversation, so switching chats in the two
              // pane layout opens the next one fresh, on its newest message
              // (SOCIAL-NEW-1), with nothing carried over from the last.
              key={search.conversationId}
              chatId={search.conversationId}
              jumpToMessageId={search.messageId}
              onBack={() => {
                void navigate({
                  search: (previous) => ({
                    ...previous,
                    conversationId: undefined,
                    messageId: undefined,
                  }),
                });
              }}
              onLeftOrDeleted={() => {
                void navigate({
                  search: (previous) => ({
                    ...previous,
                    conversationId: undefined,
                  }),
                });
              }}
            />
          )
        }
        emptyDetail={
          <SectionEmptyState headline="Pick a conversation to see it." />
        }
      />
      <NewMessageDialog
        open={overlay.isOpen("dialog", NEW_MESSAGE_DIALOG)}
        onOpenChange={(open) => {
          if (!open) overlay.close({ kind: "dialog" });
        }}
        onNewGroup={() => {
          void navigate({
            search: (previous) => ({ ...previous, dialog: NEW_CHAT_DIALOG }),
            replace: true,
          });
        }}
      />
      <ChatRowActionsDialog
        open={overlay.isOpen("dialog", CHAT_ROW_ACTIONS_DIALOG)}
        target={
          actionChatItem === null
            ? null
            : {
                chatId: actionChatItem.chatId,
                title: actionChatItem.title,
                hasUnread: actionChatItem.unreadCount > 0,
                isMuted: actionChatItem.isMuted,
              }
        }
        onOpenChange={(open) => {
          if (!open) overlay.close({ kind: "dialog", clearParams: ["chatId"] });
        }}
        onOpenChat={openConversation}
        onChooseMute={() => {
          void navigate({
            search: (previous) => ({
              ...previous,
              dialog: CHAT_ROW_MUTE_DIALOG,
            }),
            replace: true,
          });
        }}
      />
      {actionChatItem !== null ? (
        <MuteDialog
          chatId={actionChatItem.chatId}
          open={overlay.isOpen("dialog", CHAT_ROW_MUTE_DIALOG)}
          onOpenChange={(open) => {
            if (!open) {
              overlay.close({ kind: "dialog", clearParams: ["chatId"] });
            }
          }}
          isMuted={actionChatItem.isMuted}
          // The list row reads isMuted off /chat/list-mine, which the
          // dialog already refreshes, so there is no local copy to update.
          onMuteChanged={() => undefined}
        />
      ) : null}
      <NewChatDialog
        open={overlay.isOpen("dialog", NEW_CHAT_DIALOG)}
        onOpenChange={(open) => {
          if (!open) overlay.close({ kind: "dialog" });
        }}
        onCreated={(chatId) => {
          void navigate({
            search: (previous) => ({
              ...closeOverlay(previous, { kind: "dialog" }),
              conversationId: chatId,
            }),
          });
        }}
      />
      <ChatInvitationDialog
        open={overlay.isOpen("dialog", CHAT_INVITATION_DIALOG)}
        onOpenChange={(open) => {
          if (!open) {
            overlay.close({
              kind: "dialog",
              clearParams: ["chatInvitationId"],
            });
          }
        }}
        row={invitationRow}
        onAccept={() => {
          if (invitationRow === null) return;
          void handleAcceptInvitation(invitationRow.chatInvitationId);
        }}
        onDecline={() => {
          if (invitationRow === null) return;
          void handleDeclineInvitation(invitationRow.chatInvitationId);
        }}
        isBusy={acceptInvitation.isPending || declineInvitation.isPending}
      />
      <ReportMessageDialog
        open={
          search.dialog === REPORT_DIALOG &&
          search.messageId !== undefined &&
          search.senderId !== undefined
        }
        onClose={() => {
          void navigate({
            search: (prev) =>
              closeOverlay(prev, {
                kind: "dialog",
                clearParams: ["messageId", "senderId"],
              }),
            replace: true,
          });
        }}
        messageId={search.messageId ?? ""}
        senderAccountId={search.senderId ?? ""}
      />
      {search.conversationId !== undefined && isChatDetailsOpen ? (
        <ChatDetailsPanel
          chatId={search.conversationId}
          open={isChatDetailsOpen}
          onOpenChange={(open) => {
            if (!open) {
              overlay.close({ kind: "sheet", clearParams: ["panel"] });
            }
          }}
          tab={search.panel ?? "members"}
          onTabChange={(next) => {
            void navigate({
              search: (previous) => ({ ...previous, panel: next }),
            });
          }}
          onJumpToMessage={(messageId) => {
            void navigate({
              search: (previous) =>
                closeOverlay(
                  { ...previous, messageId },
                  { kind: "sheet", clearParams: ["panel"] },
                ),
              replace: true,
            });
          }}
          onDeletedForMe={() => {
            void navigate({
              search: (previous) => ({
                ...closeOverlay(previous, {
                  kind: "sheet",
                  clearParams: ["panel"],
                }),
                conversationId: undefined,
              }),
              replace: true,
            });
          }}
        />
      ) : null}
    </SectionScreen>
  );
};

export const Route = createFileRoute("/messages")({
  beforeLoad: requireSignedIn,
  validateSearch: validateMessagesSearch,
  component: MessagesScreen,
});
